/**
 * cefBrowser — the React→host control surface for the CEF host's off-screen
 * embedded browsers (tickets 027/028/029).
 *
 * The CEF host runs N real Chromium browsers off-screen and composites each one
 * into the main OSR surface at a host-controlled rect (028). React drives them
 * through the same `window.cefQuery` bridge used for IPC discovery
 * (host/src/osr.h::BridgeQueryHandler::OnQuery), but with a `{type:"browser"}`
 * request:
 *
 *   { type:"browser", action:"create",   id, url?, rect?:{x,y,w,h} }
 *   { type:"browser", action:"navigate", id, url }
 *   { type:"browser", action:"show",     id }
 *   { type:"browser", action:"hide",     id }
 *   { type:"browser", action:"resize",   id, rect:{x,y,w,h} }   // a.k.a. setRect
 *   { type:"browser", action:"focus",    id, exclusive? }
 *   { type:"browser", action:"close",    id }
 *
 * The host replies success("ok") / failure(code,message) per command, so each
 * call here returns a Promise that resolves once the host has applied it. Rects
 * are DEVICE pixels (the OSR surface is rendered at device resolution); callers
 * measure a DOM element in CSS px and multiply by devicePixelRatio.
 *
 * This module is the 030 browser API. It does NOT touch the IPC WebSocket — the
 * browser registry lives entirely host-side, reached via cefQuery, not the
 * sidecar.
 */
import { logger } from '@/lib/logger';

const TAG = 'cefBrowser';

/** A compositing rectangle in DEVICE pixels (origin = top-left of the OSR surface). */
export interface HostBrowserRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The host browser control API. One instance is shared by every BrowserTab/pane;
 * browsers are keyed by a caller-chosen string `id` (the per-tab label scheme).
 */
export interface HostBrowserControl {
  /** `context` names an isolated cookie jar (per Claude identity) so a work
   *  account's claude.ai session stays separate from personal. Omit/empty =
   *  the shared default context. */
  create(id: string, url: string, rect?: HostBrowserRect, context?: string): Promise<void>;
  navigate(id: string, url: string): Promise<void>;
  /** Open a VISIBLE browser window (URL bar) in `context` — the one-time
   *  per-identity claude.ai sign-in (a real window handles SSO the composited
   *  OSR tab suppresses). Cookies persist into `context`. */
  openWindow(id: string, url: string, context?: string): Promise<void>;
  show(id: string): Promise<void>;
  hide(id: string): Promise<void>;
  setRect(id: string, rect: HostBrowserRect): Promise<void>;
  focus(id: string, exclusive?: boolean): Promise<void>;
  close(id: string): Promise<void>;
}

/** Low-level: send one `{type:"browser"}` cefQuery and resolve on the host's reply. */
function browserQuery(payload: Record<string, unknown>): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (typeof window === 'undefined' || typeof window.cefQuery !== 'function') {
      reject(new Error('cefBrowser: window.cefQuery is not available (not running in the CEF host)'));
      return;
    }
    window.cefQuery({
      request: JSON.stringify({ type: 'browser', ...payload }),
      onSuccess: (response: string) => resolve(response),
      onFailure: (code: number, message: string) => {
        reject(new Error(`cefBrowser: ${String(payload.action)} failed (${code}): ${message}`));
      },
    });
  });
}

/**
 * Build the shared host-browser control. Every method maps 1:1 to a host
 * `{type:"browser"}` command. `create` optionally seeds the rect so the new
 * instance is positioned + shown in the same round-trip the host supports.
 */
export function createHostBrowserControl(): HostBrowserControl {
  return {
    async create(id, url, rect, context) {
      await browserQuery({ action: 'create', id, url, ...(rect ? { rect } : {}), ...(context ? { context } : {}) });
    },
    async navigate(id, url) {
      await browserQuery({ action: 'navigate', id, url });
    },
    async openWindow(id, url, context) {
      await browserQuery({ action: 'window', id, url, ...(context ? { context } : {}) });
    },
    async show(id) {
      await browserQuery({ action: 'show', id });
    },
    async hide(id) {
      await browserQuery({ action: 'hide', id });
    },
    async setRect(id, rect) {
      // "resize" and "setRect" are aliases on the host; use "resize" (029).
      await browserQuery({ action: 'resize', id, rect });
    },
    async focus(id, exclusive = true) {
      await browserQuery({ action: 'focus', id, exclusive });
    },
    async close(id) {
      await browserQuery({ action: 'close', id });
    },
  };
}

/**
 * Fire-and-forget wrapper: logs (at debug) instead of rejecting, for the many
 * layout-driven calls (setRect/show/hide) where a transient failure (browser
 * not created yet, host mid-recomposite) is not worth surfacing to the user.
 */
export function silently(p: Promise<unknown>, what: string): void {
  void p.catch((e) => logger.debug(TAG, `${what} failed (ignored)`, e));
}
